import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Avatar,
  Stack,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../Redux/Reducers/uiSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectStrategies, selectAccountingData, selectManagers } from "../../Redux/Reducers/selectors";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";

const AccountingDialog = ({ open, onClose, record }) => {
  const [fundAum, setFundAum] = useState(0);
  const [strategyAUMs, setStrategyAUMs] = useState([]);
  const [newStrategies, setNewStrategies] = useState([]);

  const strategies = useSelector(selectStrategies);
  const accountingData = useSelector(selectAccountingData);
  const managers = useSelector(selectManagers);
  const accountingId = accountingData[0]?.accountingId;
  const managerId = managers[0]?.managerId || "";
  const dispatch = useDispatch();
  
  // Determine if the current record is an SMA
  const isSMA = record?.isSMA === true;
  useEffect(() => {
    if (record && record?.strategyAUMs && record?.aum) {
      const filteredAndSortedAUMs = record?.strategyAUMs
        .filter((strategy) => strategy?.strategyAUM >= 10)
        .sort((a, b) => b.strategyAUM - a.strategyAUM);
      setStrategyAUMs(filteredAndSortedAUMs);
      setFundAum(record?.aum);
    } else {
      // Reset for new record
      setStrategyAUMs([]);
      setFundAum(0);
      setNewStrategies([]);
    }
  }, [record, open]);

  const handleFundAumChange = (e) => {
    let value = e.target.value;

    if (/^[0-9]*[.,]?[0-9]*$/.test(value)) {
      setFundAum(value);
    }
  };

  const handleStrategyAumChange = (index, value) => {
    if (/^[0-9]*[.,]?[0-9]*$/.test(value)) {
      const updatedStrategyAUMs = [...strategyAUMs];
      updatedStrategyAUMs[index] = {
        ...updatedStrategyAUMs[index],
        strategyAUM: value,
      };
      setStrategyAUMs(updatedStrategyAUMs);
    }
  };

  const addNewStrategy = () => {
    setNewStrategies([...newStrategies, { strategyId: "", strategyAUM: 0 }]);
  };

  const removeNewStrategy = (index) => {
    setNewStrategies(newStrategies.filter((_, i) => i !== index));
  };

  const handleNewStrategyChange = (index, field, value) => {
    const updated = [...newStrategies];
    if (field === "strategyAUM") {
      // For AUM field, validate input and store as string to preserve cursor position
      if (/^[0-9]*[.,]?[0-9]*$/.test(value)) {
        updated[index] = { ...updated[index], [field]: value };
      }
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setNewStrategies(updated);
  };

  const handleSave = () => {
    const updatedStrategyAUMs = {};

    // Add existing strategies
    strategyAUMs.forEach((strategy) => {
      updatedStrategyAUMs[strategy.strategyId] = parseFloat(strategy.strategyAUM) || 0;
    });

    // Add new strategies
    newStrategies.forEach((strategy) => {
      if (strategy.strategyId && parseFloat(strategy.strategyAUM) > 0) {
        updatedStrategyAUMs[strategy.strategyId] = parseFloat(strategy.strategyAUM) || 0;
      }
    });

    // Determine if this is an SMA or Fund
    let isSMA = false;
    if (record?.fund && managers && managers[0]) {
      // Check if fund exists in managers[0].smas
      if (managers[0].smas && managers[0].smas[record.fund]) {
        isSMA = true;
      }
      // If not in smas, it's in funds (isSMA remains false)
    }

    const saveData = {
      accountingId: accountingId,
      fundId: record?.fund,
      fundAUM: parseFloat(fundAum),
      strategyAUMs: updatedStrategyAUMs,
      isSMA: isSMA,
      managerId: managerId,
    };

    sendWebSocketMessage(
      JSON.stringify({
        action: "update_accounting_aum",
        data: saveData,
      })
    );
 
    dispatch(addActiveRequests("update_accounting_aum"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Updating accounting data..."));

   
    handleClose();
  };

  const handleClose = () => {
    setFundAum(0);
    setStrategyAUMs([]);
    setNewStrategies([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          pt: 2,
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: "primary.main",
                width: 48,
                height: 48,
              }}
            >
              <AccountBalanceIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: 22,
                  color: "text.primary",
                }}
              >
                Edit Accounting
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Update fund AUM and strategy allocations for {record?.name}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3} sx={{ pt: 3 }}>
          {/* Fund AUM */}
          <TextField
            fullWidth
            variant="outlined"
            label="Fund AUM"
            value={fundAum}
            onChange={handleFundAumChange}
            InputLabelProps={{
              shrink: true,
              sx: { fontWeight: 600 },
            }}
            sx={{ mt: 1 }}
          />

          {/* Strategy AUMs */}
          {strategyAUMs?.length > 0 && (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 2,
                }}
              >
                Strategy Allocations
              </Typography>

              <Box>
                {strategyAUMs?.map((strategy, index) => (
                  <Grid
                    container
                    spacing={2}
                    key={strategy.strategyId}
                    alignItems="center"
                    sx={{ mb: index < strategyAUMs.length - 1 ? 2 : 0 }}
                  >
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Strategy Name"
                        value={strategy?.strategyName || "-"}
                        disabled
                        InputLabelProps={{
                          shrink: true,
                          sx: { fontWeight: 600 },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="AUM"
                        value={strategy?.strategyAUM || ""}
                        onChange={(e) =>
                          handleStrategyAumChange(index, e.target.value)
                        }
                        InputLabelProps={{
                          shrink: true,
                          sx: { fontWeight: 600 },
                        }}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Box>
            </Box>
          )}

          {/* New Strategies */}
          {newStrategies?.length > 0 && (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 2,
                }}
              >
                New Strategy Allocations
              </Typography>

              <Box>
                {newStrategies?.map((strategy, index) => (
                  <Grid
                    container
                    spacing={2}
                    key={index}
                    alignItems="center"
                    sx={{ mb: index < newStrategies?.length - 1 ? 2 : 0 }}
                  >
                    <Grid size={{ xs: 5 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Strategy"
                        select
                        SelectProps={{ native: true }}
                        value={strategy?.strategyId}
                        onChange={(e) =>
                          handleNewStrategyChange(
                            index,
                            "strategyId",
                            e.target.value
                          )
                        }
                        InputLabelProps={{
                          shrink: true,
                          sx: { fontWeight: 600 },
                        }}
                      >
                        <option value="">Select Strategy</option>
                        {strategies
                          ?.filter((s) => {
                            // Exclude strategies that are already in strategyAUMs
                            const isAlreadyAdded = strategyAUMs?.some(
                              (existing) => existing?.strategyId === s?.strategyId
                            );
                            return !isAlreadyAdded;
                          })
                          .map((s) => (
                            <option key={s.strategyId} value={s.strategyId}>
                              {s.name}
                            </option>
                          ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 5 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="AUM"
                        value={strategy?.strategyAUM || ""}
                        onChange={(e) =>
                          handleNewStrategyChange(
                            index,
                            "strategyAUM",
                            e.target.value
                          )
                        }
                        InputLabelProps={{
                          shrink: true,
                          sx: { fontWeight: 600 },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <IconButton
                        color="error"
                        onClick={() => removeNewStrategy(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Box>
            </Box>
          )}

          {/* Add New Strategy Button - Only show for Funds, not SMAs */}
          {!isSMA && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addNewStrategy}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 600,
                alignSelf: "flex-start",
              }}
            >
              Add Strategy
            </Button>
          )}

          {/* Action Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 600,
                minWidth: 120,
              }}
            >
              Save
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default AccountingDialog;
